/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import { useLoginI18n, type LoginStrings } from './i18n';
import { FormField, inputStyle, labelStyle, primaryButtonStyle } from './atoms';

interface Props {
  email: string;
  password: string;
  remember: boolean;
  loginLoading: boolean;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onRememberChange: (v: boolean) => void;
  onLogin: () => void;
}

const PasswordField: FC<{
  t: LoginStrings;
  password: string;
  onPasswordChange: (v: string) => void;
}> = ({ t, password, onPasswordChange }) => (
  <div style={{ marginTop: 16 }}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
      }}
    >
      <label style={labelStyle}>{t.label_password}</label>
      <a
        style={{
          color: 'var(--brand-600)',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        {t.forgot_password}
      </a>
    </div>
    <input
      data-testid="login.input.password"
      value={password}
      onChange={e => onPasswordChange(e.target.value)}
      type="password"
      autoComplete="current-password"
      style={{ ...inputStyle, marginTop: 6 }}
    />
  </div>
);

const RememberCheckbox: FC<{
  t: LoginStrings;
  remember: boolean;
  onRememberChange: (v: boolean) => void;
}> = ({ t, remember, onRememberChange }) => (
  <label
    style={{
      marginTop: 14,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 13,
      color: 'var(--text-2)',
      cursor: 'pointer',
    }}
  >
    <input
      type="checkbox"
      checked={remember}
      onChange={() => onRememberChange(!remember)}
      style={{
        accentColor: 'var(--brand-500)',
        width: 14,
        height: 14,
      }}
    />
    {t.remember_30d}
  </label>
);

export const CredentialForm: FC<Props> = ({
  email,
  password,
  remember,
  loginLoading,
  onEmailChange,
  onPasswordChange,
  onRememberChange,
  onLogin,
}) => {
  const t = useLoginI18n();
  const submitDisabled = !email || !password || loginLoading;
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!submitDisabled) {
          onLogin();
        }
      }}
    >
      <FormField label={t.label_identifier}>
        <input
          data-testid="login.input.email"
          value={email}
          onChange={e => onEmailChange(e.target.value)}
          style={inputStyle}
          placeholder={t.placeholder_identifier}
          autoComplete="username"
        />
      </FormField>

      <PasswordField
        t={t}
        password={password}
        onPasswordChange={onPasswordChange}
      />

      <RememberCheckbox
        t={t}
        remember={remember}
        onRememberChange={onRememberChange}
      />

      <button
        data-testid="login.button.login"
        type="submit"
        disabled={submitDisabled}
        style={primaryButtonStyle(submitDisabled)}
        onMouseOver={e => {
          if (!submitDisabled) {
            e.currentTarget.style.background = 'var(--brand-600)';
          }
        }}
        onMouseOut={e => {
          if (!submitDisabled) {
            e.currentTarget.style.background = 'var(--brand-500)';
          }
        }}
      >
        {loginLoading ? t.submitting : t.submit}
      </button>
    </form>
  );
};
